// diagnostics.js — what the app knows that the worker cannot be asked to type.
//
// ── Why ─────────────────────────────────────────────────────────────────────
//
// Real reports from the field:
//
//     "cant see half the page"
//     "have to turn the phone sideways to hit the buttons"
//     "still cant report, says its not active"
//
// Every one is true and none is actionable. Each cost a round trip to ask
// "which screen, on what phone, how wide?" — to someone outdoors with gloves on
// who has already driven to the next yard.
//
// The browser knew all of it at the moment they pressed Send. So the worker
// keeps typing one sentence and the app fills in the rest.
//
// ── The two non-obvious ones ────────────────────────────────────────────────
//
// findOverflow() is the direct answer to "can't see half the page". Instead of
// a screenshot someone has to squint at, it reports the actual elements
// hanging off the right edge and by how many pixels:
//
//     .ap-grid > .card — 62px past the right edge
//
// That names the element AND the number, which a screenshot cannot.
//
// recentErrors() is the answer to "it says it's not active". A worker cannot
// read a console. This keeps the last few thrown errors in memory so the
// report carries what the app ACTUALLY threw rather than what they managed to
// paraphrase.
//
// ── Everything here is best-effort ──────────────────────────────────────────
//
// This runs on whatever phone a crew happens to own, including old Androids
// and locked-down iOS versions where half of these APIs are missing or throw
// on access. Every read is wrapped. A diagnostics collector that can throw
// would break the report button itself, which is strictly worse than a report
// with fewer fields.

import APP_VERSION from '../version';

const MAX_ERRORS = 5;
const MAX_OVERFLOW = 5;

/** Ring buffer of the last few errors. Module-scope so it survives re-renders. */
const errorLog = [];

function pushError(text) {
  const s = String(text || '').slice(0, 300);
  if (!s) return;
  // Consecutive duplicates are the norm — a render loop throws the same error
  // sixty times a second and would flush everything useful out of the buffer.
  if (errorLog[errorLog.length - 1] === s) return;
  errorLog.push(s);
  while (errorLog.length > MAX_ERRORS) errorLog.shift();
}

let installed = false;

/**
 * Start capturing errors. Call once, as early as possible — an error thrown
 * before this runs is not recorded, and the errors that matter most are the
 * ones during startup.
 *
 * Deliberately does NOT swallow anything: both handlers are passive listeners,
 * so React's own error overlay and the browser console behave exactly as
 * before. This only observes.
 */
export function installErrorCapture() {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('error', (e) => {
    try {
      const where = e.filename ? ` (${String(e.filename).split('/').pop()}:${e.lineno})` : '';
      // #118 §1: this was `pushError(\`${e.message}${where}\`)`. With no
      // message the template interpolates the literal string "undefined",
      // which is truthy, so pushError kept it — spending one of five slots on
      // a line that tells the reader nothing. The buffer is what a worker's
      // report carries in place of a console they cannot read, so a wasted
      // slot is an error that did not make it into the report.
      //
      // A location with no message is still worth keeping: "Unknown error
      // (main.abc123.js:42)" names the file, which is most of the answer.
      // Neither, and there is nothing to say — pushError drops the empty
      // string on its own.
      const message = typeof e.message === 'string' ? e.message.trim() : '';
      if (message) pushError(`${message}${where}`);
      else if (where) pushError(`Unknown error${where}`);
    } catch (_) { /* never let the reporter break the page */ }
  });

  window.addEventListener('unhandledrejection', (e) => {
    try {
      const r = e.reason;
      pushError(r && r.message ? `Unhandled: ${r.message}` : `Unhandled rejection: ${String(r)}`);
    } catch (_) { /* as above */ }
  });
}

/** @returns {string[]} newest last */
export function recentErrors() {
  return errorLog.slice();
}

/** Exposed for tests and for the "clear after sending" path. */
export function clearErrors() {
  errorLog.length = 0;
}

/**
 * A selector short enough to read and specific enough to find in the source.
 * Not a unique path — the goal is "which component", not "which node".
 */
function describe(el) {
  if (!el || !el.tagName) return '';
  const tag = el.tagName.toLowerCase();
  if (el.id) return `${tag}#${el.id}`;
  const cls = typeof el.className === 'string'
    ? el.className.trim().split(/\s+/).filter(Boolean).slice(0, 2).join('.')
    : '';
  return cls ? `${tag}.${cls}` : tag;
}

/**
 * Elements extending past the right edge of the viewport.
 *
 * Why the right edge only: a page that is too WIDE is the bug crews keep
 * reporting, and horizontal overflow is what makes a phone offer to zoom out.
 * Vertical overflow is just a scrollable page, which is normal.
 *
 * Only leaf-most offenders are worth reporting — if a card overflows, so does
 * every ancestor containing it, and listing all of them buries the useful one.
 * So a candidate is skipped when it has a child that also overflows.
 *
 * @returns {Array<{selector:string, px:number}>}
 */
export function findOverflow() {
  try {
    if (typeof document === 'undefined' || !document.body) return [];
    const limit = window.innerWidth;
    if (!limit) return [];

    const offenders = [];
    // Everything inside the app root. Scanning <head> and script tags wastes
    // the budget on nodes that cannot be visible.
    const all = document.body.querySelectorAll('*');
    // A big DOM on a slow phone should not stall the tap that opened the form.
    const scanned = all.length > 3000 ? Array.from(all).slice(0, 3000) : all;

    for (const el of scanned) {
      let rect;
      try { rect = el.getBoundingClientRect(); } catch (_) { continue; }
      if (!rect || rect.width === 0 || rect.height === 0) continue;
      const over = Math.round(rect.right - limit);
      // 2px of slack: sub-pixel layout rounding routinely produces 0.5px and
      // reporting that as a bug would make every report noisy.
      if (over <= 2) continue;

      // Elements the app deliberately scrolls sideways are not bugs. Tables
      // and code blocks are wrapped in overflow-x:auto on purpose.
      let style;
      try { style = window.getComputedStyle(el); } catch (_) { style = null; }
      if (style && (style.position === 'fixed' || style.visibility === 'hidden')) continue;

      offenders.push({ el, selector: describe(el), px: over });
    }

    // Drop any offender that contains another offender — report the innermost.
    const innermost = offenders.filter(
      (a) => !offenders.some((b) => b !== a && a.el.contains(b.el)),
    );

    return innermost
      .sort((a, b) => b.px - a.px)
      .slice(0, MAX_OVERFLOW)
      .map(({ selector, px }) => ({ selector, px }));
  } catch (_) {
    return [];
  }
}

/** Read a value that may not exist and may throw on access. */
const safe = (fn, fallback) => { try { const v = fn(); return v === undefined ? fallback : v; } catch (_) { return fallback; } };

/**
 * Everything worth sending with a report.
 *
 * The server re-validates all of this against an allow-list
 * (server/utils/feedbackReport.js) — nothing here is trusted just because the
 * app sent it.
 */
export function collectDiagnostics() {
  const w = typeof window === 'undefined' ? null : window;
  if (!w) return {};

  return {
    // The client's OWN version, not the server's. A PWA can serve a build
    // weeks older than the server from its service-worker cache, and "works
    // for me" almost always means exactly that. The server compares the two
    // and says so in the issue.
    clientVersion: APP_VERSION,

    viewportWidth: safe(() => w.innerWidth, undefined),
    viewportHeight: safe(() => w.innerHeight, undefined),
    screenWidth: safe(() => w.screen && w.screen.width, undefined),
    screenHeight: safe(() => w.screen && w.screen.height, undefined),
    pixelRatio: safe(() => w.devicePixelRatio, undefined),

    orientation: safe(
      () => (w.innerWidth > w.innerHeight ? 'landscape' : 'portrait'),
      undefined,
    ),

    // Installed to the home screen vs opened in a browser tab. Changes which
    // chrome is present, how much vertical space there is, and whether the
    // service worker is in play at all.
    standalone: safe(
      () => (w.matchMedia && w.matchMedia('(display-mode: standalone)').matches)
            || w.navigator.standalone === true,
      undefined,
    ),

    online: safe(() => w.navigator.onLine, undefined),
    // Chromium only — absent on iOS, which is fine, the row is then omitted.
    connection: safe(() => {
      const c = w.navigator.connection;
      return c && c.effectiveType ? String(c.effectiveType) : undefined;
    }, undefined),

    themeMode: safe(() => w.localStorage.getItem('fcp_theme_mode') || 'auto', undefined),
    language: safe(() => w.navigator.language, undefined),
    timezone: safe(() => Intl.DateTimeFormat().resolvedOptions().timeZone, undefined),

    overflow: findOverflow(),
    errors: recentErrors(),
  };
}

export default collectDiagnostics;
